import Spinners from "components/Shared/Spinners"
import React, { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { Alert, Button, ButtonGroup, Container } from "reactstrap"
import useTenantAuthChrome from "hooks/useTenantAuthChrome"
import { fetchPublicPageContentByTenantUuid } from "services/publicPageApi"
import {
  applyTenantBranding,
  clearTenantBranding,
  normalizeTenantBranding,
} from "utils/tenantBranding"
import { applyDocumentLanguage } from "utils/i18nDocument"
import { isArabicPublicLang } from "utils/pageContentUtils"
import "../publicLegalPage.scss"

const TenantPublicPageContent = ({
  tenantUuid,
  pageType,
  appType = "customer",
}) => {
  const { t, i18n } = useTranslation()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [pageContent, setPageContent] = useState(null)
  const [activeLang, setActiveLang] = useState(
    isArabicPublicLang(i18n.language) ? "sa" : "en"
  )
  const pageTitle =
    pageType === "PrivacyPolicy"
      ? t("Privacy Policy")
      : t("Terms And Conditions")

  const tenantBranding = useMemo(
    () =>
      pageContent?.tenantBranding
        ? normalizeTenantBranding(pageContent.tenantBranding)
        : null,
    [pageContent]
  )

  const documentTitle = `${pageTitle} | ${pageContent?.tenantName || ""}`.trim()
  const logoUrl = pageContent?.logoUrl || tenantBranding?.logoUrl || ""
  const isArabic = isArabicPublicLang(activeLang)

  const headerTheme = useMemo(() => {
    const theme = pageContent?.theme || {}
    return {
      backgroundColor:
        tenantBranding?.buttons?.primaryBackgroundColor ||
        theme?.buttons?.backgroundColor ||
        theme?.pageBackground?.colorStart ||
        theme?.primaryDeep ||
        undefined,
      color:
        tenantBranding?.buttons?.primaryTextColor ||
        theme?.buttons?.textColor ||
        "#ffffff",
    }
  }, [pageContent, tenantBranding])

  useTenantAuthChrome(Boolean(tenantBranding), documentTitle, tenantBranding)

  useEffect(() => {
    if (!tenantBranding) {
      return undefined
    }

    applyTenantBranding(tenantBranding)
    return () => {
      clearTenantBranding()
    }
  }, [tenantBranding])

  useEffect(() => {
    let cancelled = false

    const loadContent = async () => {
      setLoading(true)
      setError("")
      try {
        const data = await fetchPublicPageContentByTenantUuid({
          tenantUuid,
          type: pageType,
          appType,
        })
        if (!cancelled) {
          setPageContent(data)
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || t("Unable to load page content"))
          setPageContent(null)
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    loadContent()

    return () => {
      cancelled = true
    }
  }, [tenantUuid, pageType, appType])

  useEffect(() => {
    const nextLang = isArabic ? "sa" : "en"
    if (i18n.language !== nextLang) {
      i18n.changeLanguage(nextLang)
    }
    applyDocumentLanguage(nextLang)
  }, [isArabic, i18n])

  const content = useMemo(() => {
    if (!pageContent) {
      return ""
    }
    return isArabic ? pageContent.descriptionAr : pageContent.description
  }, [isArabic, pageContent])

  if (loading) {
    return (
      <div className="login-tenant public-legal-page">
        <Container className="d-flex justify-content-center py-5">
          <Spinners />
        </Container>
      </div>
    )
  }

  return (
    <div
      className={`login-tenant public-legal-page${isArabic ? " frutiger_font" : ""}`}
      dir={isArabic ? "rtl" : "ltr"}
    >
      <Container>
        <div className="public-legal-page__panel">
          <div
            className="public-legal-page__header"
            style={
              headerTheme.backgroundColor
                ? {
                    backgroundColor: headerTheme.backgroundColor,
                    color: headerTheme.color,
                  }
                : undefined
            }
          >
            <div className="public-legal-page__brand">
              {logoUrl ? (
                <img
                  src={logoUrl}
                  alt={pageContent?.tenantName || pageTitle}
                  className="public-legal-page__logo"
                />
              ) : null}
            </div>
            <h1 className="public-legal-page__title">{pageTitle}</h1>
            <div className="public-legal-page__lang">
              <div className="public-legal-page__lang-switch" dir="ltr">
                <ButtonGroup size="sm" className="public-legal-page__lang-group">
                  <Button
                    type="button"
                    className={`public-legal-page__lang-btn${
                      activeLang === "en" ? " is-active" : ""
                    }`}
                    onClick={() => setActiveLang("en")}
                  >
                    {t("English")}
                  </Button>
                  <Button
                    type="button"
                    className={`public-legal-page__lang-btn${
                      activeLang === "sa" ? " is-active" : ""
                    }`}
                    onClick={() => setActiveLang("sa")}
                  >
                    {t("Arabic")}
                  </Button>
                </ButtonGroup>
              </div>
            </div>
          </div>

          <div className="public-legal-page__body">
            {error ? <Alert color="danger">{error}</Alert> : null}

            <div
              className="public-legal-page__content"
              dir={isArabic ? "rtl" : "ltr"}
              dangerouslySetInnerHTML={{ __html: content || "" }}
            />
          </div>
        </div>
      </Container>
    </div>
  )
}

export default TenantPublicPageContent
